// Push Notification Service for Nasi iSpani


class PushNotificationService {
  constructor() {
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    this.registration = null;
    this.subscription = null;
    
    // VAPID public key - this should match your backend configuration
    this.vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || 
      'BEl62iUYgUivxIkv69yViEuiBIa40HI80NM9f4LiKiOiWjjBmMiMHpfvKCeHqQIDAQAB'; // Placeholder
  }

  /**
   * Check if push notifications are supported
   */
  isSupportedByBrowser() {
    return this.isSupported;
  }

  /**
   * Initialize the service worker and push notifications
   */
  async initialize() {
    if (!this.isSupported) {
      console.warn('Push notifications are not supported in this browser');
      return false;
    }

    try {
      // Register service worker
      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      console.log('Service Worker registered successfully:', this.registration);

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      return true;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return false;
    }
  }

  /**
   * Request permission for push notifications
   */
  async requestPermission() {
    if (!this.isSupported) {
      return 'not-supported';
    }

    try {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe() {
    if (!this.registration) {
      await this.initialize();
    }

    if (!this.registration) {
      throw new Error('Service Worker not registered');
    }

    try {
      // Check if already subscribed
      this.subscription = await this.registration.pushManager.getSubscription();

      if (!this.subscription) {
        // Create new subscription
        this.subscription = await this.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
        });

        console.log('New push subscription created:', this.subscription);
      } else {
        console.log('Existing push subscription found:', this.subscription);
      }

      // Send subscription to backend
      await this.sendSubscriptionToBackend(this.subscription);

      return this.subscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe() {
    if (!this.subscription) {
      this.subscription = await this.registration?.pushManager.getSubscription();
    }

    if (this.subscription) {
      try {
        // Unsubscribe from browser
        await this.subscription.unsubscribe();
        
        // Notify backend
        await this.removeSubscriptionFromBackend(this.subscription);
        
        this.subscription = null;
        console.log('Successfully unsubscribed from push notifications');
        return true;
      } catch (error) {
        console.error('Error unsubscribing from push notifications:', error);
        return false;
      }
    }

    return true;
  }

  /**
   * Get current subscription status
   */
  async getSubscriptionStatus() {
    if (!this.registration) {
      return { isSubscribed: false, permission: 'default' };
    }

    try {
      this.subscription = await this.registration.pushManager.getSubscription();
      const permission = Notification.permission;

      return {
        isSubscribed: !!this.subscription,
        permission: permission,
        subscription: this.subscription
      };
    } catch (error) {
      console.error('Error getting subscription status:', error);
      return { isSubscribed: false, permission: 'denied' };
    }
  }

  /**
   * Send subscription data to backend
   */
  async sendSubscriptionToBackend(subscription) {
    try {
      const subscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: this.arrayBufferToBase64(subscription.getKey('auth'))
        },
        userAgent: navigator.userAgent
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/announcements/push/subscribe/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${localStorage.getItem('token')}`,
        },
        credentials: 'include',
        body: JSON.stringify(subscriptionData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Subscription sent to backend:', result);
      return result;
    } catch (error) {
      console.error('Error sending subscription to backend:', error);
      throw error;
    }
  }

  /**
   * Remove subscription from backend
   */
  async removeSubscriptionFromBackend(subscription) {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/announcements/push/unsubscribe/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${localStorage.getItem('token')}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          endpoint: subscription.endpoint
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Subscription removed from backend:', result);
      return result;
    } catch (error) {
      console.error('Error removing subscription from backend:', error);
      throw error;
    }
  }

  /**
   * Utility function to convert VAPID key
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Utility function to convert ArrayBuffer to Base64
   */
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   * Show a test notification
   */
  async showTestNotification() {
    if (Notification.permission === 'granted') {
      const notification = new Notification('🎯 Test Notification', {
        body: 'This is a test notification from Nasi iSpani!',
        icon: '/icons/notification-icon.png',
        badge: '/icons/badge-icon.png',
        tag: 'test-notification'
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      return notification;
    } else {
      console.warn('Notification permission not granted');
      return null;
    }
  }
}

// Create and export singleton instance
const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
